import React, { useEffect, useRef, useState } from 'react';
import T from '../theme/tokens';

/**
 * Shared UI primitives. Styling lives in index.css (`.sl-btn`, `.sl-badge`,
 * `.sl-seg`, `.sl-stat`, `.sl-empty`, `.sl-skeleton`) so hover, focus-visible
 * and pressed states work without inline mouse handlers.
 */

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(' ');

/* ─── Button ─────────────────────────────────────────────────── */
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'glass';
type ButtonSize = 'sm' | 'md' | 'lg';

export const Button = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  block?: boolean;
}>(({ variant = 'secondary', size = 'md', icon, iconRight, block, className, children, type = 'button', ...rest }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cx('sl-btn', `sl-btn--${variant}`, `sl-btn--${size}`, block && 'sl-btn--block', className)}
    {...rest}
  >
    {icon && <span className="sl-btn__icon" aria-hidden>{icon}</span>}
    {children}
    {iconRight && <span className="sl-btn__icon" aria-hidden>{iconRight}</span>}
  </button>
));
Button.displayName = 'Button';

/* ─── Badge / status pill ────────────────────────────────────── */
export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

export const Badge: React.FC<{
  tone?: BadgeTone;
  dot?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({ tone = 'neutral', dot, icon, children, title, className, style }) => (
  <span className={cx('sl-badge', `sl-badge--${tone}`, className)} title={title} style={style}>
    {dot && <span className="sl-badge__dot" aria-hidden />}
    {icon && <span className="sl-badge__icon" aria-hidden>{icon}</span>}
    {children}
  </span>
);

/* ─── Segmented control (tabs / toggles) ─────────────────────── */
export function SegmentedControl<K extends string>({
  options, value, onChange, ariaLabel, size = 'md', className,
}: {
  options: readonly { key: K; label: React.ReactNode; icon?: React.ReactNode }[];
  value: K;
  onChange: (key: K) => void;
  ariaLabel: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cx('sl-seg', size === 'sm' && 'sl-seg--sm', className)}>
      {options.map((option) => {
        const active = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={active}
            className={cx('sl-seg__item', active && 'is-active')}
            onClick={() => { if (!active) onChange(option.key); }}
          >
            {option.icon && <span aria-hidden className="sl-seg__icon">{option.icon}</span>}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Count-up number ────────────────────────────────────────── */
const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Animates from the previous value to `value` in under 600ms. Falls back to the plain value. */
export const useCountUp = (value: number, durationMs = 550) => {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(0);

  useEffect(() => {
    if (!Number.isFinite(value) || reducedMotion()) {
      setDisplay(value);
      fromRef.current = value;
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (value - from) * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
      else fromRef.current = value;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, durationMs]);

  return display;
};

export const CountUp: React.FC<{ value: number; decimals?: number; suffix?: string; prefix?: string }> = ({ value, decimals = 0, suffix = '', prefix = '' }) => {
  const display = useCountUp(value);
  return <span className="sl-num">{prefix}{display.toFixed(decimals)}{suffix}</span>;
};

/* ─── Icon badge ─────────────────────────────────────────────── */
export const IconBadge: React.FC<{ tone?: BadgeTone; size?: number; children: React.ReactNode }> = ({ tone = 'brand', size = 36, children }) => (
  <span className={cx('sl-icon-badge', `sl-icon-badge--${tone}`)} style={{ width: size, height: size }} aria-hidden>
    {children}
  </span>
);

/* ─── Stat card ──────────────────────────────────────────────── */
export const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  tone?: BadgeTone;
  trend?: { value: number; label?: string; higherIsBetter?: boolean } | null;
  caption?: React.ReactNode;
  className?: string;
}> = ({ label, value, icon, tone = 'brand', trend, caption, className }) => {
  const good = trend ? (trend.higherIsBetter === false ? trend.value < 0 : trend.value > 0) : false;
  return (
    <div className={cx('sl-stat', className)}>
      <div className="sl-stat__head">
        {icon && <IconBadge tone={tone} size={30}>{icon}</IconBadge>}
        <span className="sl-eyebrow">{label}</span>
      </div>
      <div className="sl-stat__value sl-num">{value}</div>
      {(trend || caption) && (
        <div className="sl-stat__foot">
          {trend && trend.value !== 0 && (
            <span className={cx('sl-trend', good ? 'is-up' : 'is-down')}>
              <span aria-hidden>{trend.value > 0 ? '▲' : '▼'}</span> {Math.abs(trend.value)}{trend.label ?? ''}
            </span>
          )}
          {caption && <span className="sl-stat__caption">{caption}</span>}
        </div>
      )}
    </div>
  );
};

/* ─── Skeleton ───────────────────────────────────────────────── */
export const Skeleton: React.FC<{ height?: number | string; width?: number | string; radius?: number; style?: React.CSSProperties }> = ({ height = 16, width = '100%', radius = 10, style }) => (
  <div className="sl-skeleton" aria-hidden style={{ height, width, borderRadius: radius, ...style }} />
);

/* ─── Empty / error state ────────────────────────────────────── */
const EMPTY_ICONS = {
  chart: <path d="M4 19V5M4 19h16M8 15l3-4 3 2 4-6" strokeLinecap="round" strokeLinejoin="round" />,
  search: <><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4.2-4.2" strokeLinecap="round" /></>,
  file: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" strokeLinejoin="round" /><path d="M14 3v5h5" strokeLinejoin="round" /></>,
  error: <><circle cx="12" cy="12" r="9" /><path d="M12 8v4.5M12 16h.01" strokeLinecap="round" /></>,
  grid: <><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></>,
};

export const EmptyState: React.FC<{
  title: string;
  body?: React.ReactNode;
  icon?: keyof typeof EMPTY_ICONS;
  tone?: 'neutral' | 'danger';
  action?: React.ReactNode;
  compact?: boolean;
}> = ({ title, body, icon = 'chart', tone = 'neutral', action, compact }) => (
  <div className={cx('sl-empty', tone === 'danger' && 'sl-empty--danger', compact && 'sl-empty--compact')} role={tone === 'danger' ? 'alert' : 'status'}>
    <div className="sl-empty__art" aria-hidden>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{EMPTY_ICONS[icon]}</svg>
    </div>
    <div className="sl-empty__title">{title}</div>
    {body && <div className="sl-empty__body">{body}</div>}
    {action && <div style={{ marginTop: T.space[4] }}>{action}</div>}
  </div>
);
